/**
 * Creating from an agency area, as AgencyAnalytics' wizard: first who it is
 * for — a Client Report (one client) or a Roll-up Report ("multi-client,
 * multi-location, or multi-brand", clients chosen from a searchable list) —
 * then the usual starting point (blank, template or a copy) and a name.
 * Roll-up dashboards skip the first choice. Clients can be changed later in
 * the editor's "N Clients" button.
 */

import { useState, type ReactNode } from 'react';

import { ClientMultiPicker } from '../../reports/ClientMultiPicker.tsx';
import { CreateDialog } from '../../reports/CreateDialog.tsx';
import {
  reportsApi,
  type Client,
  type Page,
  type ReportDocument,
  type Template,
} from '../../reports/reports-api.ts';

type For = 'client' | 'rollup';

function rollupAsClient(count: number): Client {
  return {
    id: '',
    name: `${count} ${count === 1 ? 'client' : 'clients'}`,
    website: null,
    timezone: null,
    created_at: '',
  };
}

export function CreateFlow(props: {
  organisationId: string;
  reports: boolean;
  /** Roll-Ups area: always a roll-up. */
  rollupOnly: boolean;
  clients: Client[];
  templates: Template[];
  documents: ReportDocument[];
  onCancel: () => void;
  onCreated: (created: { document: ReportDocument; pages: Page[] }) => void;
}): ReactNode {
  const { clients } = props;
  const [audience, setAudience] = useState<For>(props.rollupOnly ? 'rollup' : 'client');
  const [clientId, setClientId] = useState(clients[0]?.id ?? '');
  const [rollupIds, setRollupIds] = useState<string[]>(
    clients.map((client) => client.id).slice(0, 50),
  );
  const [step, setStep] = useState<'who' | 'start'>('who');
  const isRollup = audience === 'rollup';
  const noun = props.reports ? 'report' : 'dashboard';

  if (step === 'start') {
    const client = isRollup
      ? rollupAsClient(rollupIds.length)
      : clients.find((item) => item.id === clientId);
    if (!client) return null;
    return (
      <CreateDialog
        client={client}
        reports={props.reports}
        templates={props.templates}
        documents={props.documents.filter(
          (document) =>
            props.reports === (document.kind !== 'DASHBOARD') &&
            (isRollup
              ? document.business_unit_id === null
              : document.business_unit_id === clientId),
        )}
        onCancel={props.onCancel}
        onCreate={async (input) => {
          props.onCreated(
            await reportsApi.create(props.organisationId, {
              business_unit_id: isRollup ? null : clientId,
              ...(isRollup ? { settings: { clients: rollupIds } } : {}),
              ...input,
            }),
          );
        }}
      />
    );
  }

  const canContinue = isRollup ? rollupIds.length > 0 : Boolean(clientId);
  return (
    <div className="rc-scrim">
      <div className="rc" role="dialog" aria-modal="true" aria-labelledby="cf-title">
        <h2 id="cf-title">{props.rollupOnly ? `New roll-up ${noun}` : `New ${noun}`}</h2>
        {!props.rollupOnly && (
          <div className="cf-choices" role="radiogroup" aria-label={`Who the ${noun} is for`}>
            {(
              [
                ['client', `Client ${props.reports ? 'Report' : 'Dashboard'}`, 'For one client.'],
                [
                  'rollup',
                  `Roll-up ${props.reports ? 'Report' : 'Dashboard'}`,
                  'Multi-client, multi-location, or multi-brand.',
                ],
              ] as const
            ).map(([value, label, hint]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={audience === value}
                className={`cf-choice${audience === value ? ' on' : ''}`}
                onClick={() => setAudience(value)}
              >
                <strong>{label}</strong>
                <small>{hint}</small>
              </button>
            ))}
          </div>
        )}
        {isRollup ? (
          <>
            <p className="panel-note">Choose clients. You can change them later in the editor.</p>
            <ClientMultiPicker clients={clients} selected={rollupIds} onChange={setRollupIds} />
          </>
        ) : (
          <label className="rs-field">
            Client
            <select value={clientId} onChange={(event) => setClientId(event.target.value)}>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="kp-dialog-actions">
          <button type="button" className="btn secondary" onClick={props.onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={!canContinue}
            onClick={() => setStep('start')}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
